/**
 * Shared gift-tier logic (1–5 gifts) for cart drawer + cart-added popup.
 * Thresholds: theme settings gift_tier_1_min … gift_tier_5_min (major currency units).
 */
(function () {
  const FALLBACK_THRESHOLDS = [0, 60, 80, 100, 150];

  function applyTextPlaceholders(template, { amount, count }) {
    if (!template) return '';
    return String(template)
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*count\s*\}\}/g, count)
      .replace(/\[amount\]/g, amount)
      .replace(/\[count\]/g, count);
  }

  function parseTierAttrsFromElement(el) {
    const read = (attr, fallback) => {
      const raw = el.getAttribute(attr);
      const v = parseFloat(raw != null && String(raw).trim() !== '' ? raw : NaN);
      return Number.isFinite(v) && v >= 0 ? v : fallback;
    };

    // Keep tier semantics (do NOT sort), then normalize to a non-decreasing sequence.
    // This guarantees ranges like:
    // 0–(t2-ε) => 1 gift, t2–(t3-ε) => 2 gifts, ... , t5+ => 5 gifts.
    let t1 = read('data-gift-tier-1-min', FALLBACK_THRESHOLDS[0]);
    let t2 = read('data-gift-tier-2-min', FALLBACK_THRESHOLDS[1]);
    let t3 = read('data-gift-tier-3-min', FALLBACK_THRESHOLDS[2]);
    let t4 = read('data-gift-tier-4-min', FALLBACK_THRESHOLDS[3]);
    let t5 = read('data-gift-tier-5-min', FALLBACK_THRESHOLDS[4]);

    t1 = Math.max(0, t1);
    t2 = Math.max(t1, t2);
    t3 = Math.max(t2, t3);
    t4 = Math.max(t3, t4);
    t5 = Math.max(t4, t5);

    return [t1, t2, t3, t4, t5];
  }

  /**
   * @param {number} totalCents
   * @param {number[]} sortedThresholds - five values [t1,t2,t3,t4,t5] ascending
   */
  function getGiftTierState(totalCents, sortedThresholds) {
    const subtotal = totalCents / 100;
    const [t1, t2, t3, t4, t5] = sortedThresholds;

    if (subtotal >= t5) {
      return {
        gifts: 5,
        progressPct: 100,
        remainingCents: 0,
        maxed: true,
      };
    }

    // Tier 1 is the default tier (1 gift) below tier 2.
    // Example: 0–59 => 1 gift, 60–79 => 2 gifts, 80–99 => 3, 100–149 => 4, 150+ => 5.
    let gifts = 1;
    let nextThreshold = t2;
    if (subtotal >= t4) {
      gifts = 4;
      nextThreshold = t5;
    } else if (subtotal >= t3) {
      gifts = 3;
      nextThreshold = t4;
    } else if (subtotal >= t2) {
      gifts = 2;
      nextThreshold = t3;
    } else {
      gifts = 1;
      nextThreshold = t2;
    }

    const safeNext = nextThreshold > 0 ? nextThreshold : 1;
    const progressPct = Math.min(100, Math.max(0, (subtotal / safeNext) * 100));
    const remaining = Math.max(0, nextThreshold - subtotal);
    const remainingCents = Math.round(remaining * 100);
    return {
      gifts,
      progressPct,
      remainingCents,
      maxed: false,
    };
  }

  function formatMoneyCents(cents) {
    const amount = Number(cents) / 100;
    try {
      const locale = document.documentElement.lang || 'it-IT';
      const currency =
        (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) || 'EUR';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
      }).format(amount);
    } catch (_) {
      return `${amount.toFixed(2)} €`;
    }
  }

  function refreshGiftTierProgress(el) {
    if (!el || el.nodeName !== 'GIFT-TIER-PROGRESS') return;

    const totalRaw = el.getAttribute('data-total-cents');
    const totalCents = parseInt(totalRaw, 10);
    const total = Number.isFinite(totalCents) ? totalCents : 0;

    const sorted = parseTierAttrsFromElement(el);
    const state = getGiftTierState(total, sorted);

    const label = el.querySelector('[data-gift-tier-label]');
    const fill = el.querySelector('[data-gift-tier-fill]');
    const track = el.querySelector('[data-gift-tier-track]');

    const textNext = el.getAttribute('data-text-next') || '';
    const textMax = el.getAttribute('data-text-max') || '';

    if (label && fill) {
      if (track) track.classList.remove('hide');
      if (state.maxed) {
        el.setAttribute('data-gift-maxed', 'true');
        const tmpl = textMax || 'Maximum gifts unlocked ([count])';
        label.textContent = applyTextPlaceholders(tmpl, {
          amount: '',
          count: String(state.gifts),
        });
      } else {
        el.removeAttribute('data-gift-maxed');
        // Copy should reference the NEXT tier (e.g. "missing X€ to receive 2 gifts" when currently at 1 gift).
        const nextGiftCount = Math.min(5, Math.max(1, state.gifts + 1));
        const tmpl = textNext || '[count] gift(s) · add [amount] for the next tier';
        label.textContent = applyTextPlaceholders(tmpl, {
          amount: formatMoneyCents(state.remainingCents),
          count: String(nextGiftCount),
        });
      }
      fill.style.width = `${state.progressPct}%`;
    }
  }

  class GiftTierProgress extends HTMLElement {
    static get observedAttributes() {
      return ['data-total-cents', 'data-text-next', 'data-text-max'];
    }

    connectedCallback() {
      refreshGiftTierProgress(this);
    }

    attributeChangedCallback() {
      refreshGiftTierProgress(this);
    }
  }

  if (!customElements.get('gift-tier-progress')) {
    customElements.define('gift-tier-progress', GiftTierProgress);
  }

  window.GiftTierProgress = {
    applyTextPlaceholders,
    parseTierAttrsFromElement,
    getGiftTierState,
    getSortedThresholds: parseTierAttrsFromElement,
    formatMoneyCents,
    refresh: refreshGiftTierProgress,
  };
})();
